import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, ArrowLeft, Users, DollarSign, Briefcase, TrendingUp, Search, ChevronRight } from 'lucide-react';
import axios from 'axios';

const t = (lang, key) => {
  const i18n = {
    en: {
      userActivity: 'User Activity', payingUsers: 'Paying Users', usersWithJobs: 'Users with Jobs',
      usersWithBids: 'Users with Bids', allUsers: 'All Users', paid: 'Paid', postedJobs: 'Posted Jobs',
      placedBids: 'Placed Bids', totalPaid: 'Total Paid', jobs: 'Jobs', bids: 'Bids',
      lastActivity: 'Last Activity', user: 'User', role: 'Role', subscription: 'Subscription',
      none: '—', searchByNameEmail: 'Search by name or email…', noUsersFound: 'No users found',
      totalRevenue: 'Total Revenue (CHF)', backToAdmin: 'Admin', search: 'Search', loading: 'Loading…'
    },
    de: {
      userActivity: 'Benutzeraktivität', payingUsers: 'Zahlende Nutzer', usersWithJobs: 'Nutzer mit Aufträgen',
      usersWithBids: 'Nutzer mit Angeboten', allUsers: 'Alle Nutzer', paid: 'Bezahlt', postedJobs: 'Aufträge erstellt',
      placedBids: 'Angebote abgegeben', totalPaid: 'Gesamt bezahlt', jobs: 'Aufträge', bids: 'Angebote',
      lastActivity: 'Letzte Aktivität', user: 'Benutzer', role: 'Rolle', subscription: 'Abonnement',
      none: '—', searchByNameEmail: 'Name oder E-Mail suchen…', noUsersFound: 'Keine Benutzer gefunden',
      totalRevenue: 'Gesamtumsatz (CHF)', backToAdmin: 'Admin', search: 'Suchen', loading: 'Lädt…'
    },
    it: {
      userActivity: 'Attività Utenti', payingUsers: 'Utenti Paganti', usersWithJobs: 'Utenti con Lavori',
      usersWithBids: 'Utenti con Offerte', allUsers: 'Tutti gli Utenti', paid: 'Pagato', postedJobs: 'Lavori Pubblicati',
      placedBids: 'Offerte Inviate', totalPaid: 'Totale Pagato', jobs: 'Lavori', bids: 'Offerte',
      lastActivity: 'Ultima Attività', user: 'Utente', role: 'Ruolo', subscription: 'Abbonamento',
      none: '—', searchByNameEmail: 'Cerca per nome o email…', noUsersFound: 'Nessun utente trovato',
      totalRevenue: 'Fatturato Totale (CHF)', backToAdmin: 'Admin', search: 'Cerca', loading: 'Caricamento…'
    },
    fr: {
      userActivity: 'Activité Utilisateurs', payingUsers: 'Utilisateurs Payants', usersWithJobs: 'Utilisateurs avec Emplois',
      usersWithBids: 'Utilisateurs avec Offres', allUsers: 'Tous les Utilisateurs', paid: 'Payé', postedJobs: 'Emplois Publiés',
      placedBids: 'Offres Soumises', totalPaid: 'Total Payé', jobs: 'Emplois', bids: 'Offres',
      lastActivity: 'Dernière Activité', user: 'Utilisateur', role: 'Rôle', subscription: 'Abonnement',
      none: '—', searchByNameEmail: 'Rechercher par nom ou email…', noUsersFound: 'Aucun utilisateur trouvé',
      totalRevenue: 'Revenu Total (CHF)', backToAdmin: 'Admin', search: 'Rechercher', loading: 'Chargement…'
    }
  };
  return (i18n[lang] || i18n.en)[key] || key;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtChf = (val) => val > 0 ? `CHF ${val.toFixed(2)}` : '—';

const SubBadge = ({ status }) => {
  const cls = {
    active: 'bg-green-100 text-green-700',
    trial: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-orange-100 text-orange-700',
    expired: 'bg-red-100 text-red-700'
  }[status] || 'bg-gray-100 text-gray-600';
  return status ? <Badge className={`${cls} border-0 text-xs`}>{status}</Badge> : <span className="text-gray-400 text-xs">—</span>;
};

const FILTERS = ['all', 'paid', 'posted_jobs', 'placed_bids'];
const FILTER_KEY = { all: 'allUsers', paid: 'paid', posted_jobs: 'postedJobs', placed_bids: 'placedBids' };

const AdminUserActivity = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { API, language } = useContext(AuthContext);
  const lang = language || 'en';
  const requestedFilter = searchParams.get('activity');
  const initialFilter = FILTERS.includes(requestedFilter) ? requestedFilter : 'all';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 50 });
      if (activeFilter !== 'all') params.set('activity', activeFilter);
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/admin/user-activity?${params}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('User activity fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [API, activeFilter, search, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const nextFilter = FILTERS.includes(requestedFilter) ? requestedFilter : 'all';
    if (nextFilter !== activeFilter) {
      setActiveFilter(nextFilter);
      setPage(1);
    }
  }, [requestedFilter, activeFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleFilterChange = (f) => {
    setPage(1);
    const nextParams = new URLSearchParams(searchParams);
    if (f === 'all') nextParams.delete('activity');
    else nextParams.set('activity', f);
    setSearchParams(nextParams, { replace: true });
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-user-activity-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/dashboard')}
            data-testid="back-to-admin-btn"
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t(lang, 'userActivity')}</h1>
            <p className="text-xs text-gray-500">{data ? `${data.total} users` : t(lang, 'loading')}</p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">{t(lang, 'payingUsers')}</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="kpi-paying-users">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : summary.paying_users ?? 0}
                  </p>
                </div>
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">{t(lang, 'totalRevenue')}</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="kpi-revenue">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `CHF ${(summary.total_revenue_chf ?? 0).toFixed(2)}`}
                  </p>
                </div>
                <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">{t(lang, 'usersWithJobs')}</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="kpi-job-posters">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : summary.users_with_jobs ?? 0}
                  </p>
                </div>
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">{t(lang, 'usersWithBids')}</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid="kpi-bidders">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : summary.users_with_bids ?? 0}
                  </p>
                </div>
                <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3" data-testid="activity-filter-tabs">
          {/* Activity tabs */}
          <div className="flex rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f === 'all' ? 'all' : f === 'posted_jobs' ? 'jobs' : f === 'placed_bids' ? 'bids' : f}`}
                onClick={() => handleFilterChange(f)}
                className={`px-4 py-2 text-sm font-medium border-r last:border-r-0 transition-colors ${
                  activeFilter === f
                    ? 'bg-red-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t(lang, FILTER_KEY[f])}
              </button>
            ))}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-[220px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="activity-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t(lang, 'searchByNameEmail')}
                className="pl-9 h-9"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              className="bg-red-600 hover:bg-red-700 h-9"
              data-testid="activity-search-btn"
            >
              {t(lang, 'search')}
            </Button>
          </form>
        </div>

        {/* Table */}
        <Card className="border-0 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-activity-empty">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">{t(lang, 'noUsersFound')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="activity-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-56">{t(lang, 'user')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t(lang, 'role')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">{t(lang, 'subscription')}</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">{t(lang, 'totalPaid')}</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">{t(lang, 'jobs')}</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">{t(lang, 'bids')}</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">{t(lang, 'lastActivity')}</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => (
                    <tr
                      key={row.user_id}
                      data-testid={`activity-row-${row.user_id}`}
                      onClick={() => navigate(`/admin/users/${row.user_id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 truncate max-w-[180px]">{row.name || '—'}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[180px]">{row.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          className={`border-0 text-xs ${row.role === 'professional' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}
                        >
                          {row.role || '—'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <SubBadge status={row.subscription_status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={row.total_paid_chf > 0 ? 'font-semibold text-green-700' : 'text-gray-400'}>
                          {fmtChf(row.total_paid_chf)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={row.jobs_count > 0 ? 'font-medium text-gray-900' : 'text-gray-400'}>
                          {row.jobs_count > 0 ? row.jobs_count : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={row.bids_count > 0 ? 'font-medium text-gray-900' : 'text-gray-400'}>
                          {row.bids_count > 0 ? row.bids_count : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-500">
                        {fmtDate(row.last_activity_at)}
                      </td>
                      <td className="px-4 py-3">
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {data && data.pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                  <p className="text-xs text-gray-500">
                    Page {data.page} / {data.pages} — {data.total} users
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                    <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminUserActivity;
